import { useState, useCallback, FormEvent, useMemo } from 'react';
import { useMutation, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type {
  IWritingStyleFormData,
  IWritingStyleFormErrors,
  IWritingStyleFormProps,
} from '@/@types/writingStyle';

const RETRYABLE_ERROR_TYPES = ['rate_limit', 'timeout', 'network_error'] as const;
const FALLBACK_ERROR_TYPES = ['paywall', 'auth_required', 'blocked'] as const;

interface IExtractionState {
  status: 'idle' | 'extracting' | 'success' | 'error';
  extractedText: string;
  extractedTitle?: string;
  error?: string;
  errorType?: string;
  canRetry: boolean;
}

export function useWritingStyleForm({
  initialData,
  onSuccess,
}: Pick<IWritingStyleFormProps, 'initialData' | 'onSuccess'>) {
  const isEditMode = useMemo(() => Boolean(initialData?._id), [initialData]);

  const [formData, setFormData] = useState<IWritingStyleFormData>({
    name: initialData?.name || '',
    inputMethod: initialData?.sampleUrl ? 'url' : 'text',
    sampleText: initialData?.sampleText || '',
    sampleUrl: initialData?.sampleUrl || '',
  });

  const [errors, setErrors] = useState<IWritingStyleFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [extraction, setExtraction] = useState<IExtractionState>({
    status: initialData?.sampleUrl ? 'success' : 'idle',
    extractedText: initialData?.sampleText || '',
    canRetry: false,
  });

  const [isEditingExtractedText, setIsEditingExtractedText] = useState(false);

  const createWritingStyle = useMutation(api.writingStyles.createWritingStyle);
  const updateWritingStyle = useMutation(api.writingStyles.updateWritingStyle);
  const analyzeWritingStyle = useAction(api.ai.styleAnalysis.analyzeWritingStyle);
  const extractTextFromUrl = useAction(api.integrations.brightdata.extractTextFromUrl);

  const handleInputMethodChange = useCallback((method: 'text' | 'url') => {
    setFormData(prev => ({ ...prev, inputMethod: method }));
    setErrors({});
    if (method === 'url') {
      setExtraction({ status: 'idle', extractedText: '', canRetry: false });
      setIsEditingExtractedText(false);
    }
  }, []);

  const handleExtractText = useCallback(async () => {
    const url = formData.sampleUrl.trim();
    if (!url) {
      setErrors(prev => ({ ...prev, sampleUrl: 'Please enter a URL' }));
      return;
    }
    try {
      new URL(url);
    } catch {
      setErrors(prev => ({ ...prev, sampleUrl: 'Please enter a valid URL' }));
      return;
    }

    setExtraction({ status: 'extracting', extractedText: '', canRetry: false });
    setErrors({});
    try {
      const result = await extractTextFromUrl({ url });
      if (result.success) {
        setExtraction({
          status: 'success',
          extractedText: result.text,
          extractedTitle: result.title,
          canRetry: false,
        });
        if (!formData.name && result.title)
          setFormData(prev => ({ ...prev, name: result.title || '' }));
      } else {
        const errorType = result.errorType || 'unknown';
        const canRetry = (RETRYABLE_ERROR_TYPES as readonly string[]).includes(errorType);
        const shouldFallback = (FALLBACK_ERROR_TYPES as readonly string[]).includes(errorType);
        setExtraction({
          status: 'error',
          extractedText: result.text || '',
          error: result.error,
          errorType,
          canRetry,
        });
        if (shouldFallback)
          setTimeout(() => {
            setFormData(prev => ({ ...prev, inputMethod: 'text' }));
          }, 3000);
      }
    } catch (error) {
      setExtraction({
        status: 'error',
        extractedText: '',
        error: error instanceof Error ? error.message : 'Failed to extract text',
        canRetry: true,
      });
    }
  }, [formData.sampleUrl, formData.name, extractTextFromUrl]);

  const handleExtractedTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setExtraction(prev => ({ ...prev, extractedText: e.target.value }));
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: IWritingStyleFormErrors = {};
    const trimmedName = formData.name.trim();
    if (!trimmedName) newErrors.name = 'Name is required';
    else if (trimmedName.length > 100) newErrors.name = 'Name must be between 1 and 100 characters';

    if (formData.inputMethod === 'text') {
      const trimmedText = formData.sampleText.trim();
      if (!trimmedText) newErrors.sampleText = 'Sample text is required';
      else {
        const wc = trimmedText.split(/\s+/).length;
        if (wc < 300)
          newErrors.sampleText = `Sample too short (${wc} words). Minimum 300 words required.`;
      }
    } else {
      if (extraction.status !== 'success' && !extraction.extractedText)
        newErrors.sampleUrl = 'Please extract text from the URL first';
      else if (extraction.extractedText) {
        const wc = extraction.extractedText.trim().split(/\s+/).length;
        if (wc < 300)
          newErrors.sampleUrl = `Extracted text too short (${wc} words). Minimum 300 words required for accurate analysis.`;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, extraction]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!validateForm()) return;
      setIsSubmitting(true);
      setErrors({});
      try {
        const sampleText =
          formData.inputMethod === 'text'
            ? formData.sampleText.trim()
            : extraction.extractedText.trim();
        if (isEditMode && initialData?._id) {
          const result = await updateWritingStyle({
            styleId: initialData._id,
            name: formData.name.trim(),
            sampleText,
            sampleUrl: formData.inputMethod === 'url' ? formData.sampleUrl.trim() : undefined,
          });
          if (!result.success) {
            setErrors({ general: result.error || 'Failed to update writing style' });
            setIsSubmitting(false);
            return;
          }
          if (result.requiresReanalysis)
            analyzeWritingStyle({ styleId: initialData._id }).catch(err =>
              console.error('AI re-analysis failed:', err)
            );
        } else {
          const result = await createWritingStyle({
            name: formData.name.trim(),
            sampleText,
            sampleUrl: formData.inputMethod === 'url' ? formData.sampleUrl.trim() : undefined,
          });
          if (!result.success || !result.styleId) {
            setErrors({ general: result.error || 'Failed to create writing style' });
            setIsSubmitting(false);
            return;
          }
          analyzeWritingStyle({ styleId: result.styleId }).catch(err =>
            console.error('AI analysis failed:', err)
          );
        }
        onSuccess();
      } catch (error) {
        setErrors({ general: error instanceof Error ? error.message : 'Unknown error occurred' });
        setIsSubmitting(false);
      }
    },
    [
      formData,
      extraction,
      validateForm,
      isEditMode,
      initialData,
      createWritingStyle,
      updateWritingStyle,
      analyzeWritingStyle,
      onSuccess,
    ]
  );

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
    setErrors(prev => ({ ...prev, name: undefined }));
  }, []);

  const handleSampleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, sampleText: e.target.value }));
    setErrors(prev => ({ ...prev, sampleText: undefined }));
  }, []);

  const handleUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, sampleUrl: e.target.value }));
    setErrors(prev => ({ ...prev, sampleUrl: undefined }));
    setExtraction({ status: 'idle', extractedText: '', canRetry: false });
    setIsEditingExtractedText(false);
  }, []);

  const textWordCount = formData.sampleText.trim().split(/\s+/).filter(Boolean).length;
  const extractedWordCount = extraction.extractedText.trim().split(/\s+/).filter(Boolean).length;
  const currentWordCount = formData.inputMethod === 'text' ? textWordCount : extractedWordCount;

  return {
    formData,
    errors,
    isSubmitting,
    isEditMode,
    extraction,
    isEditingExtractedText,
    setIsEditingExtractedText,
    textWordCount,
    extractedWordCount,
    currentWordCount,
    handleInputMethodChange,
    handleExtractText,
    handleExtractedTextChange,
    handleSubmit,
    handleNameChange,
    handleSampleTextChange,
    handleUrlChange,
  };
}
